import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, Injectable, NgZone, ElementRef } from '@angular/core';
import { ErrorStateMatcher } from '@angular/material/core';
import { UntypedFormControl, FormGroupDirective, NgForm, FormControl } from '@angular/forms';
import { UntypedFormGroup } from '@angular/forms';
import { DeviceDetectorService } from 'ngx-device-detector';
import { FIELD_TYPES } from '../../../../shared/fields/field-types';
import { Asset, BaseField } from '../../core/store/models';
import { buildPDropdownOptions } from '../../core/store/utils';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { map, startWith, take } from 'rxjs/operators';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Observable } from 'rxjs';
import { PDropdownOption } from '../../shared/interfaces/p-dropdown-options';
import { Select } from 'primeng/select';

@Injectable()
class AssetFieldErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        return !!(control && control.invalid);
    }
}

@Component({
    selector: 'ah-asset-field-input',
    templateUrl: './asset-field-input.component.html',
    styleUrls: ['./asset-field-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        { provide: ErrorStateMatcher, useClass: AssetFieldErrorStateMatcher }
    ],
    standalone: false
})
export class AssetFieldInputComponent implements AfterViewInit, OnInit, OnChanges {
    @Input() autoFocus = false;
    @Input() autoSize = true;
    @Input() controlName?: string;
    @Input() disabled = false;
    @Input() field: BaseField;
    @Input() filter? = true; // Only for multi-select fields, to turn off filtering on mobile
    @Input() fullWidth? = false;
    @Input() group: UntypedFormGroup;
    @Input() icon = null;
    @Input() iconColor: string = null;
    @Input() isButton = false;
    @Input() placeholder: string;
    @Input() required = false;
    @Input() asset?: Asset;
    @Output() change = new EventEmitter();
    @Output() blur = new EventEmitter();
    @ViewChild('input', { static: false }) inputElement;
    @ViewChild('autosize') autosize: CdkTextareaAutosize;
    @ViewChild('pSelectRef') pSelectRef!: Select;


    fieldTypes = FIELD_TYPES;
    selectedValue = '';
    _options = [];
    optionsLookup = {};
    hasBeenFocused = false;
    needsSelect = false;
    selectedOptions?: string[] = [];
    @ViewChild('optionInput', { static: false }) optionInput: ElementRef;
    @ViewChild(MatAutocompleteTrigger, { static: false }) trigger: MatAutocompleteTrigger;


    @Input() set options(options) {
        // Temporary fix for picklist so that gender's value is displayed as the label
        if (options && options.length) {
            if (options[0].hasOwnProperty('value') && options[0].hasOwnProperty('label')) {
                this._options = options;
            } else {
                this._options = buildPDropdownOptions(options, { custom: true });
            }
        } else {
            this._options = options;
        }
        this.needsSelect = this.required;
        if ((this.field.type === this.fieldTypes.PICKLIST.type || this.field.type === this.fieldTypes.MULTI_PICKLIST.type) && this._options !== null) {
            const sel = this._options.find(o => o.id === this.group.value[this.controlName]);
            if (sel) {
                this.selectedValue = sel.value;
                if (this.selectedValue !== null) {
                    this.needsSelect = false;
                }
            } else {
                this.selectedValue = this.field.name;
                if (this.required) {
                    this.selectedValue += ' *';
                }
            }
            for (const option of this._options) {
                this.optionsLookup[option.value] = option;
            }
        }
    }

    get options() {
        return this._options;
    }

    constructor(public deviceService: DeviceDetectorService, private ngZone: NgZone) { }

    ngOnInit() {
        if (this.disabled) {
            this.group.controls[this.controlName].disable();
        }
        if (this.group.value[this.controlName] !== null) {
            this.needsSelect = false;
        }
        this.setOptions(this.field)

        this.filteredOptions = this.searchControl.valueChanges.pipe(
            startWith(''),
            map(value => this._filterOptions(value))
        );
    }

    onFilter(event: any) {
        const query = event?.filter?.trim().toLowerCase();
        if (!query) {
            this._options = [...this._options];
            this.forceVirtualScrollReset();
            return;
        }
        this.forceVirtualScrollReset();
    }

    forceVirtualScrollReset() {
        setTimeout(() => {
            if (this.pSelectRef?.virtualScroll) {
                this.pSelectRef.cd.detectChanges();
            }
        }, 0);
    }

    onFilterClear() {
        this._options = [...this._options];
        this.forceVirtualScrollReset();
    }

    removeDate() {
        if (this.field.type === this.fieldTypes.DATE.type && this.field.canonical_name === this.controlName) {
            this.group.controls[this.controlName].patchValue(null);
        }
    }

    ngAfterViewInit() {
        // Wait for the view to stabilize before resizing
        this.ngZone.onStable.pipe(take(1)).subscribe(() => {
            this.autosize?.resizeToFitContent(true);
        });
        if (this.autoFocus && !this.hasBeenFocused && this.inputElement) {
            this.hasBeenFocused = true;
            setTimeout(() => {
                this.inputElement.nativeElement.focus();
            });
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.controlName) {
            this.controlName = this.field.canonical_name;
        }

        if (changes.group) {
            if (this.disabled) {
                this.group.controls[this.controlName].disable();
            }
        }
        if (changes.disabled) {
            const control = this.group.controls[this.controlName];

            if (changes.disabled.currentValue) {
                control.disable();
            } else if (control.disabled) {
                control.enable();
            }
        }
    }

    onBlur() {
        this.blur.emit();
    }

    onChange(event) {
        if (this.change) {
            this.change.emit(event.value);
        }
    }

    onMenuSelect(optionId: number) {
        const option = this.optionsLookup[optionId];
        this.group.patchValue({ [this.controlName]: option.value });
        this.selectedValue = option.label;
        this.needsSelect = option.value === null;
        if (this.change) {
            this.change.emit(option.value);
        }
    }

    setOptions(field) {
        if (field.type === FIELD_TYPES.MULTI_PICKLIST.type && field.canonical_name !== 'group_ids' && this.asset !== undefined) {
            if (this.asset[field.canonical_name] && this.asset[field.canonical_name].length) {
                this.asset[field.canonical_name].forEach(optionsId => {
                    const selectedOption = field.picklistOptions.find(g => g.id === optionsId);
                    if (selectedOption) {
                        this.selectedOptions.push(selectedOption.value);
                    }
                });
            }
        }
    }

    onMultiSelect(event, field) {
        const value = event.value.trim();
        const selectedOption = field.picklistOptions.find(option => option.label === value);
        if (selectedOption) {
            this.addOptionToSelected(value);
        }
    }

    addOptionToSelected(name: string) {
        if (!this.selectedOptions) {
            this.selectedOptions = [];
        }
        const index = this.selectedOptions.indexOf(name);
        if (index < 0) {
            this.selectedOptions.push(name);
        }
        this.selectedFieldOptions();
    }

    selectOption(event: MatAutocompleteSelectedEvent) {
        let optionName = event.option.viewValue;
        this.addOptionToSelected(optionName);
        this.searchControl.setValue('');
        if (this.optionInput && this.optionInput.nativeElement) {
            this.optionInput.nativeElement.value = '';
        }

        setTimeout(() => {
            if (this.trigger) {
                this.trigger.openPanel();
            }
        }, 100);
    }

    removeOption(option: string) {
        const index = this.selectedOptions.indexOf(option);
        if (index >= 0) {
            this.selectedOptions.splice(index, 1);
        }
        this.selectedFieldOptions();
    }


    selectedFieldOptions() {
        let selectedOptions = [];
        selectedOptions = this.selectedOptions;
        this.group.patchValue({ [this.controlName]: selectedOptions });
        if (this.change) {
            this.change.emit(selectedOptions);
        }
    }
    onDateSelect() {
        if (this.change) {
            this.change.emit();
        }
    }

    onSearchInput(event: any) {
        const value = event.target.value;
        this.searchControl.setValue(value);
        if (!value) {
            this.searchControl.setValue('');
        }
    }

    filteredOptions: Observable<PDropdownOption[]>;
    searchControl = new FormControl('');

    private _filterOptions(value: string): PDropdownOption[] {
        if (!value || typeof value !== 'string') {
            return this.options || [];
        }

        const filterValue = value.toLowerCase();
        return (this.options || []).filter(option =>
            option?.label.toLowerCase()?.includes(filterValue)
        );
    }

}
